import React, { FC, useEffect, useRef, useState } from 'react';
import ConfirmationModal from '../confirmation-modal';
import DocumentViewerModal from '../document-viewer-modal';
import { ContextMenu, ContextMenuItem, IconButton } from './ThreeDotsContextMenu.styles';
import { ReactComponent as ThreeDotsIcon } from './../../../assets/svg/Icons/ThreeDots.svg';
import { ReactComponent as ExternalIcon } from './../../../assets/svg/Icons/External.svg';
import { Icon } from 'components/atoms';
import { FileFormat } from 'helpers/documentHelper';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { useTerminologyConfig } from 'context/DataContext';
import { useTheme } from 'styled-components';
import { downloadHelper, chunkDownloadHelper } from 'helpers/downloadHelper';
import { decodedFileName } from 'helpers/strings-functions';
import { ApiError, ThreeDotsContextMenuProps } from './types';

const ThreeDotsContextMenu: FC<ThreeDotsContextMenuProps> = ({
  thirdPartyId,
  fileInfo,
  onEdit,
  onSuccessView,
  onFailView,
  onSuccessDownload,
  onFailDownload,
  onSuccessDelete,
  onFailDelete,
  showDeleteOption,
  showEditOption = false,
  onStartDownloadFile,
  onProgressDownloadFile,
  onFailDowloadFile,
  onSuccessDownloadFile,
}: ThreeDotsContextMenuProps) => {
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const contextRef = useRef<HTMLDivElement>(null);
  const { tokenInfo } = useAuth();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi, msalSubscriptionKey, xFunctionsKey } = useConfig();
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const theme = useTheme();

  const calculateDownloadProgress = (loaded: number, total: number) => {
    const pct = total > 0 ? Math.min(100, Math.floor((loaded / total) * 100)) : 0;
    onProgressDownloadFile && onProgressDownloadFile(fileInfo.id, pct);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextRef.current && !contextRef.current.contains(event.target as Node)) {
        setIsContextMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const viewerCanRenderIt =
    fileInfo.extension.includes(FileFormat.PDF) ||
    fileInfo.extension.includes(FileFormat.PNG) ||
    fileInfo.extension.includes(FileFormat.JPG) ||
    fileInfo.extension.includes(FileFormat.JPEG);

  const downloadHeaders: HeadersInit = {
    'content-type': 'application/json',
    'Ocp-Apim-Subscription-Key': `${msalSubscriptionKey}`,
    'X-functions-Key': xFunctionsKey,
    Authorization: `Bearer ${tokenInfo.accessToken}`,
  };

  const handleDownloadClick = async () => {
    setIsFileLoading(true);
    setIsContextMenuOpen(false);
    onStartDownloadFile?.(fileInfo.id);

    const { parts, downloadFailed } = await chunkDownloadHelper(
      vantageWebApi,
      `${vantageWebApi}/thirdParty/${thirdPartyId}/files/${fileInfo.id}/view`,
      fileInfo.size,
      downloadHeaders,
      onFailDownload,
      calculateDownloadProgress
    );

    if (downloadFailed) {
      onFailDowloadFile && onFailDowloadFile(fileInfo.id);
    }

    if (parts) {
      downloadHelper(decodedFileName(fileInfo.fileDownloadName), parts);
      onSuccessDownload && onSuccessDownload();
      onSuccessDownloadFile && onSuccessDownloadFile(fileInfo.id);
    }

    setIsFileLoading(false);
  };

  const handleViewClick = async () => {
    if (viewerCanRenderIt) {
      setIsDocumentViewerOpen(true);
      setIsViewLoading(true);
    }

    const { parts, fileType, downloadFailed } = await chunkDownloadHelper(
      vantageWebApi,
      `${vantageWebApi}/thirdParty/${thirdPartyId}/files/${fileInfo.id}/view`,
      fileInfo.size,
      downloadHeaders,
      onFailView
    );

    if (downloadFailed) {
      onFailDowloadFile && onFailDowloadFile(fileInfo.id);
    }

    if (parts) {
      if (viewerCanRenderIt) {
        setIsViewLoading(false);
        const blob = new Blob(parts, { type: fileType });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } else {
        downloadHelper(fileInfo.fileDownloadName, parts);
      }
      onSuccessView && onSuccessView();
    } else {
      setIsViewLoading(false);
      setIsDocumentViewerOpen(false);
      setIsContextMenuOpen(false);
    }
  };

  const handleDeleteClick = () => {
    setIsContextMenuOpen(false);
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleConfirmationOnProceed = async () => {
    setIsDeleteLoading(true);
    const response = await fetch(
      `${vantageWebApi}/thirdParty/${thirdPartyId}/files/${fileInfo.id}/delete`,
      {
        method: 'DELETE',
        headers: {
          'content-type': 'application/json',
          'Ocp-Apim-Subscription-Key': `${msalSubscriptionKey}`,
          'X-functions-Key': xFunctionsKey,
          Authorization: `Bearer ${tokenInfo.accessToken}`,
        },
      }
    );

    setIsDeleteLoading(false);

    if (response.ok) {
      onSuccessDelete && onSuccessDelete();
    } else {
      onFailDelete &&
        onFailDelete(response.status, response.type, (await response.json()) as ApiError);
    }
    setIsDeleteConfirmationModalOpen(false);
  };

  const handleOnEditClick = () => {
    setIsContextMenuOpen(false);
    onEdit();
  };

  return (
    <>
      <IconButton
        aria-label="three-dots"
        isOpen={isContextMenuOpen}
        onClick={() => setIsContextMenuOpen((prev) => !prev)}
      >
        <ThreeDotsIcon />
        {isContextMenuOpen && (
          <ContextMenu ref={contextRef} onClick={(e) => e.stopPropagation()}>
            {viewerCanRenderIt && (
              <ContextMenuItem
                title="view"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewClick();
                }}
              >
                <ExternalIcon />
                <span className="text">View</span>
              </ContextMenuItem>
            )}
            <ContextMenuItem
              title="download"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadClick();
              }}
              disabled={isFileLoading}
            >
              <Icon icon="download" size="xxs" />
              <span className="text">Download</span>
            </ContextMenuItem>
            {showEditOption && (
              <ContextMenuItem
                title="edit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOnEditClick();
                }}
              >
                <Icon icon="edit-pencil" size="xxs" />
                <span className="text">Edit</span>
              </ContextMenuItem>
            )}
            {showDeleteOption && (
              <ContextMenuItem
                title="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
              >
                <Icon icon="trash" size="xxs" colour={theme.colours.redRampage} />
                <span className="text danger">Delete</span>
              </ContextMenuItem>
            )}
          </ContextMenu>
        )}
      </IconButton>
      <DocumentViewerModal
        isOpen={isDocumentViewerOpen}
        onClose={() => setIsDocumentViewerOpen(false)}
        isLoading={isViewLoading}
        blobUrl={blobUrl}
        backMessage={`Back to ${terminologyConfig?.thirdPartySingularNaming} profile`}
      />
      <ConfirmationModal
        isOpen={isDeleteConfirmationModalOpen}
        onClose={() => setIsDeleteConfirmationModalOpen(false)}
        onProceed={handleConfirmationOnProceed}
        onCancel={() => setIsDeleteConfirmationModalOpen(false)}
        title="Delete Confirmation"
        message="Please note that this document will be permanently deleted. Do you want to proceed?"
        proceedButtonText="Delete"
        proceedButtonLoading={isDeleteLoading}
        onProceedAriaLabel="Delete"
        loading={false}
      />
    </>
  );
};

export default ThreeDotsContextMenu;
